import * as React from 'react';
import { Editor as TinyMCEComponent } from '@tinymce/tinymce-react';
import { Editor as TinyMCEEditor } from 'tinymce';

import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';

import { useTheme } from '@mui/material/styles';

import {
  API_URL,
  EMPTY_STRING,
  formatBytes
} from '../global';

import {
  HTTPHeaders,
  RichTextEditorRef
} from '../interfaces';

import {
  EmailAttachment
} from '../models';

// Hooks
import { useAuth0User } from '../hooks/UseAuth0User';

/**
 * Defines the properties accepted by the RichTextEditor component.
 */
interface RichTextEditorProps {
  /** * The starting HTML content to be loaded into the editor on mount. 
   * This is typically used for drafts or editing existing messages.
   */
  initialValue?: string;
  clear?: () => void;
  getAttachments?: () => EmailAttachment[];
  getContent?: () => string;
  onChange?: (content: string) => void;
  reset?: () => void;
}

const RichTextEditor = React.forwardRef<RichTextEditorRef, RichTextEditorProps>((props, ref) => {
  // 1. Destructure props for easier access within the component.
  // initialValue: The starting HTML content for the editor.
  // onChange: A callback function to notify the parent of content changes.
  const {
    initialValue,
    onChange,
  } = props;

  // 2. Specialized hooks for infrastructure.
  // useAuth0User: Custom hook to fetch the JWT bearer token for secure API calls.
  // useSnackbar: Notistack hook to trigger pop-up notifications (success/error).
  const { getAccessToken } = useAuth0User();
  const { enqueueSnackbar } = useSnackbar();

  // 3. Component Local State.
  // emailAttachments: Stores an array of uploaded file metadata (names, IDs, sizes).
  // This state drives the list of attachment chips/icons below the editor.
  const [emailAttachments, setEmailAttachments] = React.useState<Array<EmailAttachment>>([]);

  // 4. React Refs (Reference objects).
  // editorRef: Directly accesses the TinyMCE instance to get/set content imperatively.
  // fileInputRef: Accesses the hidden <input type="file" /> to programmatically trigger the file picker.
  const editorRef = React.useRef<TinyMCEEditor | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // 5. Configuration constants.
  // Editor: Aliasing the TinyMCE component for use in the JSX.
  // apiKey: The license key required for TinyMCE Cloud services.
  const Editor = TinyMCEComponent as any;
  const apiKey = '3gt3ef6jo75zktcs9uz6gmquy0q3e2bo8q91z02ao5bm7n5x'; // Tinymce API key

  // #region Styles

  const theme = useTheme();
  const styles = {
    attachments: {
      display: 'flex',
      flexwrap: 'wrap',
      gap: theme.spacing(1),
      margin: theme.spacing(1, 0, 1, 1)
    },
    files: {
      display: 'none'
    }
  };

  // #endregion

  /**
   * Exposes specific internal methods to the parent component via the forwardRef.
   * This allows the parent to interact with the editor without managing its internal state.
   */
  /**
     * Exposes specific internal methods to the parent component via the forwardRef.
     */
  React.useImperativeHandle(ref, () => ({
    /**
     * Retrieves the current HTML content.
     */
    getContent: () => {
      return editorRef.current ? editorRef.current.getContent() : '';
    },

    /**
     * Provides the parent with the current list of attachments.
     */
    getAttachments: () => {
      return emailAttachments;
    },

    /**
     * ONLY clears the text content of the editor.
     * Use this if you want to keep attachments but wipe the message.
     */
    clear: () => {
      if (editorRef.current) {
        editorRef.current.setContent(EMPTY_STRING);
      }
    },

    /**
     * Resets the entire component (Content + Attachments + File Input).
     */
    reset: () => {
      if (editorRef.current) {
        editorRef.current.setContent(EMPTY_STRING);
      }

      setEmailAttachments([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = EMPTY_STRING;
      }
    }
  }), [emailAttachments]);

  /**
   * Removes an attachment from the local state based on its index.
   * This updates the UI list and ensures the file is no longer included 
   * when the parent calls getAttachments().
   */
  const removeAttachment = (index: number) => {
    // 1. Create a new array excluding the item at the specified index.
    // We use the functional update pattern to ensure we're working with the latest state.
    const updatedList = emailAttachments.filter((_, i) => i !== index);

    // 2. Update the local state to refresh the UI list.
    setEmailAttachments(updatedList);

    // 3. Optional: If your validation logic depends on the number of attachments,
    // trigger the onChange callback to notify the parent of the state change.
    if (onChange && editorRef.current) {
      onChange(editorRef.current.getContent());
    }
  };

  /**
     * Handles the selection, validation, and upload of a file from the user's device.
     * This function orchestrates Auth0 authentication, multipart/form-data construction,
     * and updates the internal state upon a successful API response.
     */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // 1. Extract the first file from the input selection.
    const file = e.target.files?.[0];
    if (!file) return;

    // 2. Define allowed extensions for client-side filtering.
    const allowedExtensions = ['pdf', 'doc', 'docx', 'jpg', 'png'];

    // 3. Extract the file extension by splitting the name at the last dot.
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    // 4. Validate the file type before proceeding with the upload.
    // If invalid, alert the user and clear the input to allow a retry.
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      enqueueSnackbar(
        `Invalid file type. Only ${allowedExtensions.join(', ')} are allowed.`,
        { variant: 'error' }
      );

      if (fileInputRef.current) fileInputRef.current.value = EMPTY_STRING;
      return;
    }

    // 5. Retrieve the Auth0 Access Token to authorize the secure API request.
    const accessToken = await getAccessToken();

    // 6. Configure the request headers. 
    // Note: 'Content-Type' is omitted here because the browser automatically 
    // sets 'multipart/form-data' and the boundary string when it sees a FormData body.
    const headers: HTTPHeaders = {};
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const url = new URL(`${API_URL}/v1/email/attachment`);

    // 7. Prepare the payload as Multipart Form Data.
    // Ensure the key 'File' matches the IFormFile property name in the .NET backend.
    const formData: FormData = new FormData();
    formData.append('File', file);

    // 8. Execute the asynchronous POST request to the attachment endpoint.
    await fetch(url.toString(), {
      method: 'POST',
      body: formData,
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        // 9. Verify if the server returned a 2xx success status code.
        if (!response.ok) {
          throw new Error('Attachment not uploaded');
        }
        return response.json();
      })
      .then((attachment: EmailAttachment) => {
        // 10. Convert the raw byte size from the server into a human-readable format (e.g., "1.2 MB").
        attachment.size = formatBytes(parseInt(attachment.size));

        // 11. Append the new attachment metadata to the existing local state.
        const updatedList = [...emailAttachments, attachment];
        setEmailAttachments(updatedList);

        // 12. Notify the user of the successful upload.
        enqueueSnackbar(`${file.name} (${formatBytes(file.size)}) attachment added`, { variant: 'success' });

        // 13. Trigger the onChange callback so the parent component can re-validate 
        // the form (e.g., enabling the submit button now that an attachment exists).
        if (onChange && editorRef.current) {
          onChange(editorRef.current.getContent());
        }
      })
      .catch(() => {
        // 14. Handle network-level errors or explicit 4xx/5xx failures.
        enqueueSnackbar(`WARNING: ${file.name} was not added as an attachment`, { variant: 'error' });
      })
      .finally(() => {
        // 15. Cleanup: Always reset the file input value. 
        // This ensures the 'onChange' event fires even if the user re-selects the same file.
        if (fileInputRef.current) {
          fileInputRef.current.value = EMPTY_STRING;
        }
      });
  };

  return (
    <>
      {/* Hidden file input controlled by the toolbar button */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        // List the allowed extensions or MIME types here
        accept=".pdf,.doc,.docx,.jpg,.png"
      />

      <Editor
        apiKey={apiKey}
        initialValue={initialValue}
        onInit={(_evt: any, editor: TinyMCEEditor) => {
          editorRef.current = editor;
        }}
        onEditorChange={(content: string) => {
          if (onChange) onChange(content);
        }}
        init={{
          height: 350,
          menubar: false,
          statusbar: false,
          branding: false,
          promotion: false,
          plugins: 'advlist autolink lists link charmap searchreplace visualblocks table paste',
          toolbar: 'undo redo | blocks | ' +
            'bold italic forecolor | alignleft aligncenter ' +
            'alignright alignjustify | bullist numlist outdent indent | ' +
            'removeformat | attachBtn',

          setup: (editor: TinyMCEEditor) => {
            editor.ui.registry.addButton('attachBtn', {
              icon: 'upload',
              tooltip: 'Attach File (PDF)',
              onAction: () => {
                // Click the hidden input
                fileInputRef.current?.click();
                //if (onAttachClick) onAttachClick();
              }
            });
          },
          placeholder: 'Enter content here...',
          content_style: `
            body { font-family: Arial; font-size: 14px; }
            .attachment-link { display: inline-flex; align-items: center; gap: 4px; padding: 2px 4px; background: #f5f5f5; border-radius: 4px; }
          `,
          inline_styles: true,
          formats: {
            alignleft: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { 'text-align': 'left' }, attributes: { align: 'left' } },
            aligncenter: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { 'text-align': 'center' }, attributes: { align: 'center' } },
            alignright: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { 'text-align': 'right' }, attributes: { align: 'right' } },
            alignjustify: { selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table,img', styles: { 'text-align': 'justify' }, attributes: { align: 'justify' } },
          },
          extended_valid_elements: 'p[style|align],span[style],div[style|align],td[style|align]',
          forced_root_block: 'p',
          convert_urls: false,
          // Updated to include 'download' and 'rel' for the attachment links
          valid_elements: 'a[href|target=_blank|download|rel|style|class],strong/b,em/i,br,p,ul,ol,li,span[style],img[src|alt|width|height],table,thead,tbody,tr,td[style]',
        }}
      />
      <Box sx={styles.attachments}>
        {emailAttachments.map((attachment, index) => (
          <div key={index} style={{
            display: 'flex',
            alignItems: 'center',
            padding: '4px 8px',
            background: '#f0f2f5',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px'
          }}>
            <div style={{ textDecoration: 'none', color: '#1976d2' }}>
              {attachment.attachmentFileName}
            </div>
            <button
              onClick={() => removeAttachment(index)}
              style={{ marginLeft: '8px', border: 'none', background: 'none', cursor: 'pointer', color: 'red' }}
            >
              ✕
            </button>
          </div>
        ))}
      </Box>
    </>
  );
});

export default RichTextEditor;